import { c as _c } from "react/compiler-runtime";
/**
 * Worktree switcher section of the DevPill. Extracted from dev-pill.tsx
 * as part of Phase 6 of the round-3 enactment plan (review-9016e0cc-83b #7).
 *
 * Imported only by dev-pill.tsx's PillUI composer. The
 * `dev-pill-sections/` directory must NOT contain an index.ts barrel — direct
 * file imports only, per .claude/rules/typescript.md § "Barrel files".
 */
import { useClickOutside } from '@fpo3/ts-commons-ui';
import { ChevronUp, Trees } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DevProxyAdapter, WorktreeOption } from '../adapters';
export interface WorktreeSectionState {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  options: WorktreeOption[];
  pickWorktree: (value: string) => void;
}

/** Fetches available worktree options when the popup opens and provides a callback to switch the active worktree via the dev-proxy adapter. */
export function useWorktreeSection(adapter, ready) {
  const $ = _c(16);
  const [open, setOpen] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [options, setOptions] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== adapter || $[2] !== open || $[3] !== ready) {
    t1 = () => {
      if (!open || !ready) {
        return;
      }
      let cancelled = false;
      adapter.getOptions().then(result => {
        if (!cancelled) {
          setOptions(result);
        }
      }).catch(() => {
        if (!cancelled) {
          setOptions([]);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t2 = [open, ready, adapter];
    $[1] = adapter;
    $[2] = open;
    $[3] = ready;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== adapter) {
    t3 = value => {
      adapter.switchWorktree(value);
      setOpen(false);
    };
    $[6] = adapter;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const pickWorktree = t3;
  let t4;
  if ($[8] !== open || $[9] !== options || $[10] !== ready) {
    t4 = open && ready ? options : [];
    $[8] = open;
    $[9] = options;
    $[10] = ready;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const effectiveOptions = t4;
  let t5;
  if ($[12] !== effectiveOptions || $[13] !== open || $[14] !== pickWorktree) {
    t5 = {
      open,
      setOpen,
      options: effectiveOptions,
      pickWorktree
    };
    $[12] = effectiveOptions;
    $[13] = open;
    $[14] = pickWorktree;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}

/** Renders the worktree switcher button with a click-outside-dismissable popup listing available worktrees. */
export function WorktreeSection(t0) {
  const $ = _c(19);
  const {
    section,
    wrapRef
  } = t0;
  let t1;
  if ($[0] !== section) {
    t1 = () => section.setOpen(false);
    $[0] = section;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const close = t1;
  useClickOutside(wrapRef, section.open, close);
  const t2 = section.open ? "none" : "rotate(180deg)";
  let t3;
  if ($[2] !== t2) {
    t3 = {
      transition: "transform 0.2s",
      transform: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const chevronStyle = t3;
  const t4 = `section${section.open ? " active" : ""}`;
  let t5;
  if ($[4] !== section) {
    t5 = () => section.setOpen(_temp);
    $[4] = section;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Trees size={14} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== chevronStyle) {
    t7 = <ChevronUp size={11} style={chevronStyle} />;
    $[7] = chevronStyle;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t7) {
    t8 = <button type="button" className={t4} onClick={t5}>{t6}{t7}</button>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== section) {
    t9 = section.open && <div className="wt-popup">{section.options.map(option => <button type="button" key={option.value} className={`wt-item${option.selected ? " current" : ""}${option.disabled ? " disabled" : ""}`} disabled={option.disabled || option.selected} onClick={() => section.pickWorktree(option.value)}><span>{option.label}</span>{option.port && <span className="wt-port">:{option.port}</span>}</button>)}</div>;
    $[13] = section;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9 || $[17] !== wrapRef) {
    t10 = <div className="section-wrap worktree-wrap" ref={wrapRef}>{t8}{t9}</div>;
    $[15] = t8;
    $[16] = t9;
    $[17] = wrapRef;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function _temp(prev) {
  return !prev;
}